import { ref, computed, useTemplateRef } from 'vue'
import { useNavigation } from '@el-plus/hooks/use-navigation'
import { useLocale } from '@el-plus/hooks/use-locale'
import { downloadFile, previewFile, importFile } from '@el-plus/utils/file'
import { Cookies } from '@el-plus/utils/cookie'
import type { TableInstance } from '@el-plus/components/table'

export const useAttachment = (props, { data }) => {
  const { mode: defaultMode } = useNavigation()
  const { t } = useLocale()
  const mode = computed(() => {
    return props.mode || defaultMode.value
  })
  const tableRef = useTemplateRef<TableInstance>('tableRef')
  const isNormalOpen = props.openType === 'normal'
  const isDialogOpen = props.openType === 'dialog'

  // 待确认的附件列表
  let toBeConfirmData: Record<string, any>[] = []
  // 待删除的附件列表
  let toBeDeleteData: {
    [key: number]: Record<string, any> // key要存储原来索引以便保留原顺序
  } = {}
  const actionButtons = ref<any>([
    {
      name: t('ep.attachment.download'),
      onClick({ row }) {
        downloadFile({
          src: row.filePath,
          fileName: row.originalFilename || row.originalFileName,
        })
      },
    },
    {
      name: t('ep.attachment.delete'),
      disabled: () => {
        return mode.value === 'browse'
      },
      onClick: ({ $index, row }) => {
        if (isDialogOpen) {
          // 待删除的附件列表
          toBeDeleteData[$index] = row
        }
        data.value.splice($index, 1)
      },
    },
  ])
  const columns = ref([
    {
      label: t('ep.attachment.attachmentType'),
      prop: 'type',
      type: 'EpSelect',
      required: true,
      show: () => props.isType,
      props: {
        desc: ({ row }) => {
          return row.typeDesc
        },
      },
    },
    {
      label: t('ep.attachment.fileName'),
      prop: 'originalFilename',
      type: 'EpLink',
      onClick({ row }) {
        previewFile(row.filePath)
      },
    },
    {
      label: t('ep.attachment.creator'),
      prop: 'createBy',
    },
    {
      label: t('ep.attachment.createTime'),
      prop: 'createTime',
    },
    {
      label: t('ep.attachment.description'),
      prop: 'note',
      type: 'EpInput',
      show: () => props.isNote,
    },
  ])
  const addAttachment = async () => {
    toBeConfirmData = []
    const fileData = await importFile({
      api: 'api-f/fast/files/save',
      extraData: {
        createBy: Cookies.get('accountName') || '',
      },
    })
    fileData.fileId = fileData.id
    delete fileData.id
    if (isDialogOpen) {
      // 待确认的附件列表
      toBeConfirmData.push(fileData)
    }
    if (props.beforeAdd) {
      await props.beforeAdd?.(fileData)
    }
    data.value.push(fileData)
  }
  // 确认
  const onConfirm = async (resolve) => {
    await tableRef.value?.validate()
    resolve(data.value)
    toBeConfirmData = []
    toBeDeleteData = {}
  }
  // 取消
  const onCancel = (resolve) => {
    if (toBeConfirmData.length) {
      // 找到待确认的附件
      const index = data.value.findIndex((item) => {
        return item.fileId === toBeConfirmData[0].fileId
      })
      if (index !== -1) {
        // 删除待确认的附件
        data.value.splice(index, 1)
      }
      // 清空待确认的附件列表
      toBeConfirmData = []
    }
    if (Object.keys(toBeDeleteData).length) {
      // 找到待删除的附件在原位置插入
      Object.keys(toBeDeleteData).forEach((oldIndex) => {
        data.value.splice(Number(oldIndex), 0, toBeDeleteData[oldIndex])
      })
      // 清空待删除的附件列表
      toBeDeleteData = {}
    }
    resolve()
  }
  const permission = computed(() => {
    const arr = props.permission?.split(':') || []
    return {
      arg: arr[0],
      value: arr[1]
    }
  })
  return {
    columns,
    actionButtons,
    addAttachment,
    isNormalOpen,
    isDialogOpen,
    onConfirm,
    onCancel,
    mode,
    permission
  }
}
